import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 非同期(IndexedDB / AuthedApi)データ取得のための共通フックと無効化バス。
 * 書き込み後に invalidateStores(...storeNames) を呼ぶと、該当ストアを購読中の
 * フックが再取得する。SetCache後の再取得と同じ仕組み。
 */

type Listener = () => void;

const storeListeners = new Map<string, Set<Listener>>();

export function subscribeStores(stores: string[], listener: Listener): () => void {
  for (const store of stores) {
    let set = storeListeners.get(store);
    if (!set) {
      set = new Set<Listener>();
      storeListeners.set(store, set);
    }
    set.add(listener);
  }
  return () => {
    for (const store of stores) {
      storeListeners.get(store)?.delete(listener);
    }
  };
}

export function invalidateStores(...stores: string[]): void {
  const notified = new Set<Listener>();
  for (const store of stores) {
    const set = storeListeners.get(store);
    if (!set) {
      continue;
    }
    for (const listener of set) {
      notified.add(listener);
    }
  }
  for (const listener of notified) {
    listener();
  }
}

export type AsyncState<T> = {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
};

export type UseAsyncDataOptions = {
  stores?: string[];
  deps?: unknown[];
  enabled?: boolean;
};

export function useAsyncData<T>(loader: () => Promise<T>, options: UseAsyncDataOptions = {}): AsyncState<T> {
  const stores = options.stores ?? [];
  const deps = options.deps ?? [];
  const enabled = options.enabled ?? true;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<unknown>(null);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const requestIdRef = useRef(0);

  const run = useCallback(() => {
    if (!enabled) {
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    loaderRef.current()
      .then((result) => {
        if (requestId === requestIdRef.current) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (requestId === requestIdRef.current) {
          setError(e);
          setLoading(false);
        }
      });
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      return;
    }
    requestIdRef.current = requestIdRef.current + 1;
    setData(null);
    setError(null);
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    run();
    return () => {
      requestIdRef.current = requestIdRef.current + 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, ...deps]);

  useEffect(() => {
    if (stores.length === 0) {
      return;
    }
    return subscribeStores(stores, run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, stores.join(",")]);

  return { data, loading, error, reload: run };
}

export function useEntities<T>(stores: string[], loader: () => Promise<T>, deps: unknown[] = [], enabled: boolean = true): AsyncState<T> {
  return useAsyncData(loader, { stores: stores, deps: deps, enabled: enabled });
}
